/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Subject } from 'rxjs';
import { filter, startWith, takeUntil } from 'rxjs/operators';
import { map } from 'rxjs/operators';

import { WindowService } from '@digital-platform/rs-analytics/services';
import { AnalyticsService } from '@digital-platform/rs-analytics/services';
import { User } from '@digital-platform/rs-auth/domain';
import { IdentityService } from '@digital-platform/rs-auth/services';
import { CanadianCustomerService } from '@digital-platform/rs-cart/services';
import { HeaderResponse, NavLinks } from '@digital-platform/rs-cms/domain';
import { NavigationHelperService } from '@digital-platform/rs-cms/feature-ptypes';
import { CmsContentService } from '@digital-platform/rs-cms/services';
import { CmsServiceAlertsService } from '@digital-platform/rs-cms/services';
import { ServiceAlertsEntity } from '@digital-platform/rs-cms/services';
import { mockServiceAlertsData } from '@digital-platform/rs-cms/services';
import { mockNotificationData } from '@digital-platform/rs-notifications/services';
import { ViewerCountryService } from '@digital-platform/rs-shared/services';

@UntilDestroy()
@Component({
  selector: 'digital-platform-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  public identityService: IdentityService = inject(IdentityService);
  public userState?: User['state'];
  public pageContent: HeaderResponse[0] | undefined;
  public pageContentOriginal: HeaderResponse[0] | undefined;
  public icons: string[] = [];
  public mockNotificationData = mockNotificationData;
  public mockServiceAlertData = mockServiceAlertsData;

  private navigationHelperService = inject(NavigationHelperService);
  private cmsContentService = inject(CmsContentService);
  public cmsServiceAlertsService: CmsServiceAlertsService = inject(
    CmsServiceAlertsService
  );

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private cookieService = inject(CookieService);
  private analyticsService = inject(AnalyticsService);
  private canadianCustomerService = inject(CanadianCustomerService);
  private destroyed$ = new Subject<void>();
  private viewerCountryService = inject(ViewerCountryService);
  private windowService = inject(WindowService);

  constructor() {
    this.icons = [
      'rs-icon-money',
      'rs-icon-calendar',
      'rs-icon-question',
      this.cmsServiceAlertsService.hasEntities
        ? 'rs-icon-exclamation-solid'
        : 'rs-icon-exclamation',
    ];
  }

  public ngOnInit() {
    this.toggleCanadaModal();

    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter((event: any) => event instanceof NavigationEnd),
        startWith(this.router)
      )
      .subscribe((value) => {
        // Only register page analytics for non JS pages
        if (value.url) {
          const regex = /[?;]/g;
          const splitUrl = value.url.split(regex);

          let rootUrl = splitUrl[0];

          rootUrl = rootUrl === '/home' ? '/' : rootUrl;

          this.analyticsService.registerPageName(rootUrl);
        }
      });

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe((params) => {
        const googleClickId = params['gclid'];

        // Get gclid from query params and store it as cookie
        if (googleClickId) {
          this.cookieService.set('gclid', googleClickId.toString(), 30);
        }
      });

    this.cmsContentService
      .getHeaderContent()
      .pipe(map((response: HeaderResponse) => response[0]))
      .subscribe((headerResponse: HeaderResponse[0]) => {
        this.pageContentOriginal = headerResponse;
        // Generate initial link urls when page content is loaded
        this.generateLinkUrls();
      });

    this.identityService.state$
      .pipe(untilDestroyed(this))
      .subscribe((user: User) => {
        // Only generate link urls when user state changes
        if (this.userState !== user.state) {
          this.userState = user.state;

          // Generate link urls when user state changes
          this.generateLinkUrls();
        }
      });

    this.cmsServiceAlertsService.state$
      .pipe(untilDestroyed(this))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe((serviceAlertsEntity: ServiceAlertsEntity[]) => {
        this.toggleServiceAlertsIcon();
      });
  }

  public toggleCanadaModal() {
    const currentUrl$ = this.router.events.pipe(
      takeUntil(this.destroyed$),
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    );

    combineLatest([this.viewerCountryService.viewerCountry$, currentUrl$])
      .pipe(untilDestroyed(this))
      .subscribe(([countryCode, url]) => {
        this.windowService.nativeWindow.countryCode = countryCode;
        if (countryCode === 'CA') {
          if (url !== '/blog' && url !== '/preferences') {
            this.canadianCustomerService.openCanadianCustomerModal();
          }
        }
      });
  }

  public toggleServiceAlertsIcon(): void {
    // Update service-alerts exclamation icon
    if (
      this.icons[3] === 'rs-icon-exclamation' ||
      this.icons[3] === 'rs-icon-exclamation-solid'
    ) {
      this.icons[3] = this.cmsServiceAlertsService.hasEntities
        ? 'rs-icon-exclamation-solid'
        : 'rs-icon-exclamation';
    }
  }

  public navigate(event: Event, url: string | null): void {
    this.navigationHelperService.navigate(event, url ?? '', true);
  }

  private generateLinkUrls(): void {
    // Reset the page content `link_urls` to the original state. Use deep clone to avoid modifying the original object
    this.pageContent = cloneDeep(this.pageContentOriginal);

    if (this.pageContent) {
      for (const navLinks of this.pageContent.utility_links) {
        // Modify the page content `link_urls` to the generated link url
        navLinks.link_url = this.generateLinkUrl(navLinks);
      }
    }
  }

  private generateLinkUrl(item: NavLinks): NavLinks['link_url'] {
    let linkUrl;
    if (this.identityService.isLoggedIn) {
      // Conditional link url based on user state
      switch (this.userState) {
        case 'AS1': {
          linkUrl = item?.link_url_as1;
          break;
        }
        case 'AS2': {
          linkUrl = item?.link_url_as2;
          break;
        }
        case 'AS3': {
          linkUrl = item?.link_url_as3;
          break;
        }
        case 'AS4': {
          linkUrl = item?.link_url_as4;
          break;
        }

        default: {
          linkUrl = item.link_url;
          break;
        }
      }
    } else {
      // Logged out
      linkUrl = item.link_url;
    }

    return linkUrl;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
